import * as React from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';

import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import type * as hiveCore from '@gitbook/hive-core';
import { useHiveBranch } from '@gitbook/hive-react';
import {
    Dialog,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    DialogHeader,
    FieldError,
    Form,
    Heading,
    Label,
    Submit,
    TextField,
    TextInput,
    useDialogAction,
} from '@gitbook/spine-react';
import { assertIsDefined } from '@gitbook/util-ts';

import { useLoggedInUser } from '../../state';

type RenameFileFormInputs = {
    name: string;
};

interface RenameFileDialogProps {
    space: api.Space;
    file: hiveCore.RevisionFile;
}

/**
 * Modal to rename a file in a revision.
 */
export function RenameFileDialog(props: RenameFileDialogProps): React.ReactElement {
    return (
        <Dialog>
            <DialogHeader>
                <Heading>Rename file</Heading>
            </DialogHeader>
            <RenameFileForm {...props} />
        </Dialog>
    );
}

function RenameFileForm(props: RenameFileDialogProps) {
    const { space, file } = props;

    const nameInputRef = React.useRef<HTMLInputElement>(null);
    const [isPending, startDialogAction] = useDialogAction();
    const hiveBranch = useHiveBranch();
    const user = useLoggedInUser();

    const form = useForm<RenameFileFormInputs>({
        values: {
            name: file.name,
        },
    });

    const onSubmit: SubmitHandler<RenameFileFormInputs> = async ({ name }) => {
        assertIsDefined(hiveBranch);

        await startDialogAction(async ({ close }) => {
            if (name !== file.name) {
                analytics.logEvent(
                    analytics.createSpaceEvent(space, {
                        type: 'edit_rename_file',
                    })
                );
                await hiveBranch.change((change) => {
                    change.updateFile(file.id, {
                        name,
                    });
                    change.markFileAsEdited(file.id, user.uid);
                });
            }

            close?.();
        });
    };

    // Select the part without the extension when the modal is opened
    React.useEffect(() => {
        // Request an animation frame to trigger the focus. Since it's a modal,
        // it will render in a parent set to `display: none`, which prevents focus
        // from working. In this case, we need to wait for react to change the DOM and then focus
        // the element.
        const animationFrameIndex = requestAnimationFrame(() => {
            // Focus on first render in the next animation frame so the parent has time
            // to set itself to display block
            if (nameInputRef.current) {
                nameInputRef.current.setSelectionRange(0, file.name.lastIndexOf('.'));
                nameInputRef.current.focus();
            }
        });

        return () => {
            cancelAnimationFrame(animationFrameIndex);
        };
    }, [nameInputRef, file.name]);

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
            <DialogBody>
                <TextField name="name" required minLength={1} maxLength={50}>
                    <Label>Name</Label>
                    <TextInput
                        ref={nameInputRef}
                        autoFocus
                        placeholder="file.txt"
                        disabled={isPending}
                    />
                    <FieldError />
                </TextField>
            </DialogBody>
            <DialogFooter>
                <DialogCloseButton />
                <Submit label="Rename" />
            </DialogFooter>
        </Form>
    );
}
